import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Package,
  Shirt,
  Crown,
  ShieldCheck,
  Footprints,
  Hand,
  ArrowRight,
} from "lucide-react";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import { siteConfig } from "@/config/siteConfig";
import { combinedProductionItems } from "@/data/productsData";

const ICONS = { Package, Shirt, Crown, ShieldCheck, Footprints, Hand };

const ProductionConsumablesPage = () => {
  const path = "/products/production-consumables";
  const canonical = `${siteConfig.DOMAIN}${path}`;
  const webPageSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: "Production Consumables",
    url: canonical,
    description:
      "Complementary production consumables for pharmaceutical manufacturing environments.",
  };

  return (
    <>
      <SEO
        rawTitle={`Production Consumables | ${siteConfig.COMPANY_NAME}`}
        description="Complementary pharmaceutical production consumables — tapes, gloves, caps, masks, shoe covers and disposable garments — supplied internationally from Dubai."
        path={path}
        extraSchema={webPageSchema}
      />
      <Helmet>
      </Helmet>

      <div className="mx-auto max-w-[1240px] px-6 lg:px-10 pt-8 lg:pt-10">
        <Breadcrumbs
          items={[
            { label: "Home", to: "/" },
            { label: "Products", to: "/products/blister-pack-violet-ink" },
            { label: "Production Consumables" },
          ]}
        />
      </div>

      <section
        className="mx-auto max-w-[1240px] px-6 lg:px-10 pt-8 lg:pt-10 pb-6"
        data-testid="production-consumables-hero"
      >
        <p className="eyebrow" style={{ color: "var(--slate)" }}>
          Complementary Products
        </p>
        <h1
          className="font-heading mt-4 text-[32px] leading-tight lg:text-[44px] lg:leading-[1.05] font-semibold text-[var(--navy)]"
          data-testid="production-consumables-h1"
        >
          Production Consumables
        </h1>
        <p className="mt-6 text-[16px] leading-relaxed text-[var(--charcoal)] max-w-3xl">
          A selection of complementary production consumables available to
          pharmaceutical manufacturing environments. These items are secondary
          to our printing and coding portfolio and are provided alongside those
          core solutions on request.
        </p>
      </section>

      <section
        className="mx-auto max-w-[1240px] px-6 lg:px-10 pb-12"
        data-testid="production-consumables-grid"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {combinedProductionItems.map((item) => {
            const Icon = ICONS[item.iconKey] || Package;
            return (
              <article
                key={item.slug}
                className="border border-[var(--border)] rounded-[4px] p-6 bg-white flex flex-col"
                data-testid={`consumable-${item.slug}`}
              >
                <Icon
                  strokeWidth={1.5}
                  style={{ width: 28, height: 28, color: "var(--slate)" }}
                  aria-hidden="true"
                />
                <h2 className="font-heading mt-4 text-[17px] font-semibold text-[var(--charcoal)]">
                  {item.name}
                </h2>
                {item.subnote && (
                  <p className="mt-1 text-[12px] italic text-[var(--slate)]">
                    {item.subnote}
                  </p>
                )}
                <p className="mt-3 text-[14px] leading-relaxed text-[var(--slate)] flex-1">
                  {item.desc}
                </p>
                <Link
                  to={`/contact?product=${item.slug}`}
                  className="arrow-link mt-4 text-[13px]"
                  data-testid={`consumable-${item.slug}-enquire`}
                >
                  Enquire <ArrowRight className="w-4 h-4" />
                </Link>
              </article>
            );
          })}
        </div>
      </section>

      <section
        className="mx-auto max-w-[1240px] px-6 lg:px-10 pb-20"
        data-testid="production-consumables-callout"
      >
        <div className="border-t border-[var(--border)] pt-8">
          <p className="text-[14px] italic text-[var(--slate)] max-w-3xl">
            Product availability and pack sizes vary. Contact our team to
            confirm current options for your market.
          </p>
        </div>
      </section>
    </>
  );
};

export default ProductionConsumablesPage;
